import { Component, Inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AdminService } from '../../services/AdminService/admin';
import { GeneralTermResponse } from '../../models/country-config.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';

export interface UpsertGeneralTermDialogData {
  term?: GeneralTermResponse;
}

@Component({
  selector: 'app-upsert-general-term-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    ErrorBanner,
  ],
  templateUrl: './upsert-general-term-dialog.html',
  styleUrl: './upsert-general-term-dialog.scss',
})
export class UpsertGeneralTermDialog {
  editing: GeneralTermResponse | null;
  term = '';
  description = '';

  isSubmitting = signal(false);
  errorMessage = signal('');

  constructor(
    private dialogRef: MatDialogRef<UpsertGeneralTermDialog>,
    private admin: AdminService,
    @Inject(MAT_DIALOG_DATA) data: UpsertGeneralTermDialogData
  ) {
    this.editing = data.term ?? null;
    this.term = this.editing?.term ?? '';
    this.description = this.editing?.description ?? '';
  }

  get canSubmit(): boolean {
    return !!this.term.trim();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');

    const request$ = this.editing
      ? this.admin.updateGeneralTerm(this.editing.id, { description: this.description || undefined })
      : this.admin.createGeneralTerm({ term: this.term.trim(), description: this.description || undefined });

    request$.subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to save general term.'));
      },
    });
  }
}
